import axios from 'axios'

import type {
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from 'axios'

export interface ApiResponse<T = unknown> {
  success: boolean
  data?: T
  error?: string
  code?: string
}

const TOKEN_KEY = 'bitland_token'

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY)

export const setToken = (token: string): void => localStorage.setItem(TOKEN_KEY, token)

export const removeToken = (): void => localStorage.removeItem(TOKEN_KEY)

/**
 * 首页 / 活动 / 市场：未登录或 token 失效时允许继续浏览，不因 401 整页跳转到登录。
 * （下单、支付等操作仍可在各自流程里提示登录。）
 */
export function isPublicBrowsePath(pathname: string): boolean {
  if (pathname === '/' || pathname === '') {
    return true
  }
  if (pathname.startsWith('/activity')) {
    return true
  }
  if (pathname.startsWith('/market')) {
    return true
  }
  return false
}

const createInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => Promise.reject(error),
  )

  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      const { data } = response

      if (!data.success) {
        return Promise.reject(new Error(data.error || 'Request failed'))
      }

      return response
    },
    (error) => {
      if (error.response) {
        const { status } = error.response
        switch (status) {
          case 401:
            removeToken()
            if (typeof window !== 'undefined' && !isPublicBrowsePath(window.location.pathname)) {
              window.location.href = '/login'
            }
            break
          case 403:
            console.error('[API] Forbidden')
            break
          case 404:
            console.error('[API] Not Found')
            break
          case 500:
            console.error('[API] Internal Server Error')
            break
          default:
            console.error(`[API] Error: ${status}`)
        }
      } else if (error.request) {
        console.error('[API] Network Error')
      }

      return Promise.reject(error)
    },
  )

  return instance
}

const request = createInstance()

export const get = <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse<T>>> => request.get<ApiResponse<T>>(url, config)

export const post = <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse<T>>> => request.post<ApiResponse<T>>(url, data, config)

export const put = <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse<T>>> => request.put<ApiResponse<T>>(url, data, config)

export const patch = <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse<T>>> => request.patch<ApiResponse<T>>(url, data, config)

export const del = <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<ApiResponse<T>>> => request.delete<ApiResponse<T>>(url, config)

export default request
